import React from 'react';
import { compose, withProps, withStateHandlers } from 'recompose';
import styled, { StylesProps } from '../../../../../lib/styles';
import { Brand } from '../../../store/selectors';
import Typography from '../../../../../components/presentational/Typography';
import Button from '../../../../../components/presentational/Button';
import Spacer from '../../../../../components/presentational/Spacer';
import { trackEvent } from '../../../../../lib/ga';
import { ONBOARDING_STARTED, ONBOARDING_CARD_CLICKED } from '../../../lib/ga';
import Icon from '../../../../../components/presentational/Icon';

import onboardingBlockStyles from './OnboardingBlock_styles';
import Image from '../../../../../components/presentational/Image';
import { Account } from '../../../../../models/user';
import { getAccount } from '../../../../../lib/accountUtils';
import { callOnMount } from '../../../../../behaviour/patterns';

interface NavigationCard {
  imageURL: string;
  url: string;
  title: string;
}

interface ValueProposition {
  title: string;
  text: string;
  imageURL: string;
}

interface Partner {
  title: string;
  imageURL: string;
  href: string;
}

export interface OnboardingBlockData {
  type: 'onboarding_block';
  title?: string;
  description?: string;
  navigationCardsTitle: string;
  navigationCards?: NavigationCard[];
  valuePropositionsTitle: string;
  valuePropositions?: ValueProposition[];
  partners?: Partner[];
}

export interface OnboardingBlockProps {
  block: OnboardingBlockData;
  user?: Account | false;
  onStart: () => any;
  brand: Brand;
}

const OnboardingBlock = ({
  onStart,
  user,
  block: {
    navigationCardsTitle,
    navigationCards = [],
    valuePropositionsTitle,
    valuePropositions = [],
    partners = [],
  },
  styles,
}: OnboardingBlockProps & StylesProps<typeof onboardingBlockStyles>) => (
  <div>
    <div className={styles.top}>
      <div className={styles.background} />
      <div className={styles.foreground}>
        <div className={styles.inner}>
          <Spacer space={'xs'}>
            <Typography
              align={'center'}
              color={'white'}
              className={styles.scrim}
            >
              <h1>
                Join the community that will get you through the hard times
                ahead.
              </h1>
            </Typography>

            <Typography
              align={'center'}
              color={'white'}
              weight={'normal'}
              className={styles.scrim}
            >
              <h3>
                Starts at 60 is the community you need when Covid-19 is changing
                life as we know it. We stick together, help each other, share
                information and have a whole lot of fun online.
              </h3>

              <h3 className={styles.tagline}>
                <strong>
                  Join for interactive online events, expert advice, timely
                  news, great deals and community conversation.
                </strong>
              </h3>
            </Typography>
          </Spacer>
        </div>

        <Button
          className={styles.startTop}
          color={'secondary'}
          href={'/membership'}
          onClick={onStart}
        >
          <Spacer row space={'xs'} align={'flex-end'}>
            <Icon icon={'explore'} />

            {user ? (
              <span>Membership</span>
            ) : (
              <span>Join&nbsp;now,&nbsp;it's&nbsp;free</span>
            )}
          </Spacer>
        </Button>
      </div>
    </div>

    {(navigationCards.length > 0 || valuePropositions.length > 0) && (
      <div className={styles.bottom}>
        <Spacer space={'l'} width={1160} selfT={'s'} align={'center'}>
          {navigationCards.length > 0 && (
            <Spacer space={'s'}>
              <Typography align={'center'}>
                <h3>{navigationCardsTitle}</h3>
              </Typography>

              <div className={styles.navigationCards}>
                {navigationCards.map(({ url, imageURL, title }) => (
                  <a
                    key={title}
                    href={url}
                    onClick={() =>
                      trackEvent(ONBOARDING_CARD_CLICKED, {
                        transport: 'beacon',
                      })
                    }
                    className={styles.navigationCard}
                  >
                    <Image
                      src={imageURL}
                      alt={title}
                      width={50}
                      height={50}
                      resizeWidth={50}
                      aspect={[1, 1]}
                    />

                    <div className={styles.navigationCardTitle}>{title}</div>
                  </a>
                ))}
              </div>
            </Spacer>
          )}

          {valuePropositions.length > 0 && (
            <Spacer space={'s'}>
              <Typography align={'center'}>
                <h3>{valuePropositionsTitle}</h3>
              </Typography>

              <div className={styles.valuePropositions}>
                {valuePropositions.map(({ title, text, imageURL }) => (
                  <div key={title} className={styles.valueProposition}>
                    <Spacer space={'s'} align={'center'}>
                      <Image
                        src={imageURL}
                        width={120}
                        height={120}
                        resizeWidth={120}
                        aspect={[1, 1]}
                        alt={title}
                      />

                      <Spacer space={'xs'}>
                        <Typography weight={'bold'} align={'center'}>
                          <h4>{title}</h4>
                        </Typography>

                        <Typography align={'center'}>
                          <h5>{text}</h5>
                        </Typography>
                      </Spacer>
                    </Spacer>
                  </div>
                ))}
              </div>
            </Spacer>
          )}

          <Button
            color={'primary'}
            href={'/membership'}
            onClick={onStart}
            className={styles.startBottom}
          >
            <Spacer row space={'xs'} align={'flex-end'}>
              <Icon icon={'explore'} />

              {user ? (
                <span>Membership</span>
              ) : (
                <span>Join&nbsp;now,&nbsp;it's&nbsp;free</span>
              )}
            </Spacer>
          </Button>
        </Spacer>
      </div>
    )}

    {partners.length > 0 && (
      <div className={styles.partnerContainer}>
        <Typography>
          <h2>Proudly sponsored by</h2>
        </Typography>

        <div className={styles.partners}>
          {partners.map(({ title, imageURL, href }) => (
            <a key={title} href={href} title={title} target={'_blank'}>
              <Image
                lazy
                src={imageURL}
                alt={title}
                height={100}
                resizeHeight={100}
              />
            </a>
          ))}
        </div>
      </div>
    )}
  </div>
);

export default compose(
  withStateHandlers(({}) => ({ user: undefined }), {
    loadUser: () => () => ({
      user: getAccount() || false,
    }),
  }),
  withProps(() => ({
    onStart: () => {
      trackEvent(ONBOARDING_STARTED, { transport: 'beacon' });
    },
  })),
  callOnMount('loadUser'),
  styled(onboardingBlockStyles)
)(OnboardingBlock);
